import axios from "axios";
import { readPaginationHeaders, toApiPage, toPageIndex } from "../utils/apiPagination";
import type { PriceListUsage, UserLimits, UserTariffPurchase, UserTariffRequest } from "./user";

export interface AdminUserRequestedFor {
  inn?: string;
  siteUrl?: string;
}

export interface AdminUser {
  id: number;
  email?: string;
  alias?: string;
  emailConfirmed?: boolean;
  roles?: string[];
  createdAt?: string;
  locked?: boolean;
  failedAttempts?: number;
  freeTariffUsed?: boolean;
  requestedFor?: AdminUserRequestedFor[];
  userLimits?: UserLimits | null;
  requestedTariffs?: UserTariffRequest[];
  activeTariffs?: PriceListUsage[];
  archivedTariffs?: PriceListUsage[] | null;
  purchases?: UserTariffPurchase[];
  purchaseHistory?: UserTariffPurchase[];
}

export interface AdminUserListParams {
  page?: number;
  pageSize?: number;
  email?: string;
  showAll?: boolean;
}

export interface AdminUserListResponse {
  items: AdminUser[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export const fetchAdminUsers = (params: AdminUserListParams = {}): Promise<AdminUserListResponse> => {
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const apiPage = toApiPage(pageIndex);

  const searchParams = new URLSearchParams();
  searchParams.set("page", String(apiPage));
  searchParams.set("pageSize", String(pageSize));

  const email = params.email?.trim();
  if (email) {
    searchParams.set("email", email);
  }

  if (params.showAll) {
    searchParams.set("showAll", "true");
  }

  const queryString = searchParams.toString();

  return axios.get<AdminUser[]>(`/admin/users${queryString ? `?${queryString}` : ""}`).then((resp) => {
    const items = Array.isArray(resp.data) ? resp.data : [];
    const pagination = readPaginationHeaders(resp.headers ?? {}, {
      page: apiPage,
      pageSize,
      itemsLength: items.length,
    });

    return {
      items,
      page: toPageIndex(pagination.page),
      pageSize: pagination.pageSize,
      totalPages: pagination.totalPages,
      totalElements: pagination.totalElements,
    };
  });
};

export const grantAdminRole = (userId: number): Promise<void> =>
  axios.post(`/admin/user/${userId}/admin-role`).then(() => undefined);

export const revokeAdminRole = (userId: number): Promise<void> =>
  axios.delete(`/admin/user/${userId}/admin-role`).then(() => undefined);

export const downloadAdminUsersExcel = (): Promise<Blob> =>
  axios
    .get("/admin/users/excel/users.xlsx", { responseType: "blob" })
    .then((resp) => resp.data);
